import { storage } from '../utils/storage.js';

const DEFAULT_STATE = {
  target: null,
  history: [],
  questionCount: 0,
  guessCount: 0,
  score: 0,
  bestScore: 0,
  bestScoreCelebrity: 0,
  bestScoreDetective: 0,
  difficulty: 'medium',
  field: 'any',
  gameMode: 'celebrity',
  stage: 'idle',
  lastError: null,
};

const CELEBRITY_BASE_SCORE = {
  easy: 200,
  medium: 400,
  hard: 600,
};

const DETECTIVE_BASE_SCORE = {
  easy: 300,
  medium: 600,
  hard: 900,
};

export function createGameState() {
  let state = {
    ...DEFAULT_STATE,
    bestScoreCelebrity: storage.config.get('bestScore') || 0,
    bestScoreDetective: storage.config.get('bestScoreDetective') || 0,
  };

  state.bestScore = state.gameMode === 'detective' ? state.bestScoreDetective : state.bestScoreCelebrity;

  // 页面刷新后恢复当前对局
  const savedTarget = storage.game.get('target');
  const savedHistory = storage.game.get('history');
  const savedMode = storage.game.get('gameMode');

  if (savedTarget) {
    state.target = savedTarget;
    state.history = Array.isArray(savedHistory) ? savedHistory : [];
    state.gameMode = savedMode || state.gameMode;
    state.questionCount = state.history.filter(h => h.type === 'question').length;
    state.guessCount = state.history.filter(h => h.type === 'guess').length;
    state.stage = state.history.some(h => h.type === 'guess' && h.correct) ? 'won' : 'playing';
    state.bestScore = state.gameMode === 'detective' ? state.bestScoreDetective : state.bestScoreCelebrity;
  }

  return {
    get: () => ({ ...state }),

    setConfig({ difficulty, field, gameMode }) {
      state.difficulty = difficulty ?? state.difficulty;
      state.field = field ?? state.field;
      if (gameMode !== undefined && gameMode !== state.gameMode) {
        state.gameMode = gameMode;
        state.bestScore = state.gameMode === 'detective' ? state.bestScoreDetective : state.bestScoreCelebrity;
      }
    },

    setTarget(target) {
      state.target = target;
      state.history = [];
      state.questionCount = 0;
      state.guessCount = 0;
      state.stage = 'playing';
      state.lastError = null;
      storage.game.set('target', target);
      storage.game.set('history', []);
      storage.game.set('gameMode', state.gameMode);
    },

    addQuestion(result) {
      state.history.push(result);
      state.questionCount += 1;
      storage.game.set('history', state.history);
      return { ...state };
    },

    addGuess(result) {
      state.history.push(result);
      state.guessCount += 1;

      if (result.correct) {
        state.stage = 'won';
        if (state.gameMode === 'detective') {
          state.score += calculateDetectiveScore(
            state.difficulty,
            state.questionCount,
            state.guessCount,
            result.accuracy || 0,
          );
          if (state.score > state.bestScoreDetective) {
            state.bestScoreDetective = state.score;
            state.bestScore = state.bestScoreDetective;
            storage.config.set('bestScoreDetective', state.bestScoreDetective);
          }
        } else {
          state.score += calculateCelebrityScore(state.difficulty, state.questionCount, state.guessCount, true);
          if (state.score > state.bestScoreCelebrity) {
            state.bestScoreCelebrity = state.score;
            state.bestScore = state.bestScoreCelebrity;
            storage.config.set('bestScore', state.bestScoreCelebrity);
          }
        }
      }

      storage.game.set('history', state.history);
      return { ...state };
    },

    giveUp() {
      state.stage = 'lost';
      storage.game.set('history', state.history);
    },

    newGame() {
      state.target = null;
      state.history = [];
      state.questionCount = 0;
      state.guessCount = 0;
      state.stage = 'idle';
      state.lastError = null;
      storage.game.remove('target');
      storage.game.remove('history');
      storage.game.remove('gameMode');
    },

    setError(err) {
      state.lastError = err;
    },
  };
}

function calculateCelebrityScore(difficulty, questionCount, guessCount, won) {
  if (!won) return 0;
  const base = CELEBRITY_BASE_SCORE[difficulty] || 400;
  const questionPenalty = questionCount * 5;
  const wrongGuessPenalty = Math.max(guessCount - 1, 0) * 30;
  return Math.max(base - questionPenalty - wrongGuessPenalty, 10);
}

function calculateDetectiveScore(difficulty, questionCount, guessCount, accuracy) {
  const base = DETECTIVE_BASE_SCORE[difficulty] || 600;
  const questionPenalty = questionCount * 8;
  const attemptPenalty = Math.max(guessCount - 1, 0) * 80;
  const accuracyBonus = Math.round(accuracy * 500);
  return Math.max(base - questionPenalty - attemptPenalty + accuracyBonus, 10);
}
